// Libraries
import { useEffect, useState } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { getRoute } from '@utils/Paths';
import { Routes } from '@components/paths';
// Redux
import { RootState } from '@redux/rootReducer';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { setCustomLoader } from '@redux/pila/pila.slice';
// Api
import {
    postGeneratePayroll,
    setConfirmed,
    setErrorModal,
    setErrorGeneratePayrollModal,
    getTransmitedPayrollByPeriod,
    setIsAlreadyTransmitedThisPeriod,
    getPeriodsByYear,
    postSocialBenefitsConfiguration,
} from '@redux/electronic-payroll/electronicPayroll.slice';
// Models
import { IValidateSalary } from '@models/ValidateSalary';
import { IDataTablePayroll } from '@models/ElectronicPayroll';
import { IDataErrors } from '../components/models/ModalMistake';
import { IPeriods } from '@models/Periods';
// Constants
import {
    ELECTRONIC_PAYROLL_NOVELTY_TRANSMISION_ERROR,
    ELECTRONIC_PAYROLL_BUY_NEW_PLANS,
    WITHOUT_TRANSMIT,
} from '../constants/Transmit';
// Utils
import { capitalizeFirstLetter } from '@utils/Text';
import { IOptionsInformation } from '../components/models/ModalInformationImportant';

export const useTransmitElectronicPayroll = (): {
    periods: IPeriods[];
    currentDate: IPeriods;
    dataPayroll: IDataTablePayroll[];
    confirmed: boolean;
    transmitConfirmed: boolean;
    errorModal: boolean;
    noveltyTransmisionError: boolean;
    errorGeneratePayrollModal: boolean;
    dataErrorModal: IDataErrors[];
    buyNewPlan: boolean;
    showSocialBenefitsModal: boolean;
    socialBenefitsConfigurationValues: IOptionsInformation;
    handleGeneratePayroll: () => Promise<void>;
    handleOpenModalError: () => void;
    handleOpenModalErrorGenerate: () => void;
    handleChangeCard: (item: number) => void;
    handleGetDateModal: () => string;
    handleRedirectPayrollPlan: () => void;
    setShowSocialBenefitsModal: (value: boolean) => void;
    handleConfirmSocialBenefitsConfiguration: (
        serviceBonus: string,
        severeance: string,
        electronic_payroll_vacation_periodicity: string
    ) => void;
} => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {
        periods: { currentPeriod },
        electronicPayroll: { payroll, confirmed, errorModal, responseConfirm, errorGeneratePayrollModal, periods },
        session: { loginUser },
        company: { information },
    } = useAppSelector((state: RootState) => state);
    const [dataPayroll, setDataPayroll] = useState<IDataTablePayroll[]>([]);
    const [transmitConfirmed, setTransmitConfirmed] = useState<boolean>(confirmed);
    const [dataErrorModal, setDataErrorModal] = useState<IDataErrors[]>([]);
    const currentPeriods: IPeriods[] = periods;
    const currentMonth = moment().format('MM');
    const currentYear = moment().year();
    const monthCode = currentYear + '-' + currentMonth;
    const monthFilter: IPeriods = periods.length ? periods.filter(elem => elem.code == monthCode)[0] : currentPeriod;
    const [currentDate, setCurrentDate] = useState<IPeriods>(monthFilter);
    const [noveltyTransmisionError, setNoveltyTransmisionError] = useState<boolean>(false);
    const [showSocialBenefitsModal, setShowSocialBenefitsModal] = useState<boolean>(false);
    const [buyNewPlan, setBuyNewPlan] = useState<boolean>(false);
    const socialBenefitsConfigurationValues = {
        service_bonus: information.service_bonus || null,
        severance: information.severance || null,
        electronic_payroll_vacation_periodicity: information.electronic_payroll_vacation_periodicity || null,
    };

    useEffect(() => {
        loginUser?.lastLogin && dispatch(getPeriodsByYear());
    }, []);

    useEffect(() => {
        const monthFilter: IPeriods = periods.filter(elem => elem.code == monthCode)[0];
        if (monthFilter) setCurrentDate(monthFilter);
    }, [periods]);

    useEffect(() => {
        setTransmitConfirmed(false);
    }, [responseConfirm, currentDate]);

    useEffect(() => {
        if (currentDate?.period?.id) {
            dispatch(getTransmitedPayrollByPeriod(currentDate?.period?.id));
        }
    }, [currentDate]);

    useEffect(() => {
        formatDataPayroll();
        if (payroll?.response_service !== null && payroll?.response_service !== undefined) {
            const payrollErrors = JSON.parse(payroll?.response_service)?.descripcion;
            setNoveltyTransmisionError(payrollErrors.includes(ELECTRONIC_PAYROLL_NOVELTY_TRANSMISION_ERROR));
        }
    }, [payroll, transmitConfirmed]);

    useEffect(() => {
        setBuyNewPlan(false);
        responseConfirm && handleDataErrorModal();
        if (responseConfirm?.descripcion?.[0]?.includes(ELECTRONIC_PAYROLL_BUY_NEW_PLANS)) {
            setBuyNewPlan(true);
        }
    }, [responseConfirm]);

    /**
     * Format data table from electronic payroll
     */
    const formatDataPayroll = (): void => {
        if (payroll && payroll?.json_data) {
            const baseData = JSON.parse(payroll?.json_data);
            const tableData: IDataTablePayroll[] = [];

            const payrollItem = {
                documents_quantity: baseData?.Trabajador?.length || 0,
                number_employees: getNumberEmployees(baseData?.Trabajador),
                total_accruals: baseData?.Trabajador?.reduce(
                    (prev: number, curr: IValidateSalary) => curr?.DevengadosTotal + prev,
                    0
                ),
                total_discounts: baseData?.Trabajador?.reduce(
                    (prev: number, curr: IValidateSalary) => curr?.DeduccionesTotal + prev,
                    0
                ),
                total_salaries: baseData?.Trabajador?.reduce(
                    (prev: number, curr: IValidateSalary) => parseInt(curr?.Sueldo) + prev,
                    0
                ),
                period: moment(baseData?.Periodo?.FechaLiquidacionInicio, 'YYYY-MM-DD').format('MM/YYYY'),
                state: capitalizeFirstLetter(payroll?.status) || WITHOUT_TRANSMIT,
            };

            tableData.push(payrollItem);
            setDataPayroll(tableData);
            dispatch(setConfirmed(payroll.is_success));
            setTransmitConfirmed(true);
        } else {
            setDataPayroll([]);
            setTransmitConfirmed(false);
        }
    };

    /**
     * Get number employees
     * @param {IValidateSalary[]} data - Data list to count
     * @returns {number} - Number employees
     */
    const getNumberEmployees = (data: IValidateSalary[]): number => {
        const employees: IValidateSalary[] = [];

        data.forEach(employee => {
            const index = employees?.findIndex(item => item?.NumeroDocumento === employee?.NumeroDocumento);
            if (index === -1) {
                employees.push(employee);
            }
            return employees;
        });

        return employees.length;
    };

    /**
     * Action to generate payroll
     */
    const handleGeneratePayroll = async (): Promise<void> => {
        await dispatch(postGeneratePayroll(currentDate?.period.id));
        dispatch(setIsAlreadyTransmitedThisPeriod(false));
        setTransmitConfirmed(true);
    };

    /**
     * Action to open error modal from confirm electronic payroll
     */
    const handleOpenModalError = (): void => {
        dispatch(setErrorModal());
    };

    /**
     * Action to open error modal from generate electronic payroll
     */
    const handleOpenModalErrorGenerate = (): void => {
        dispatch(setErrorGeneratePayrollModal());
    };

    /**
     * Action to format data modal error when you confirm electronic payroll
     */
    const handleDataErrorModal = (): void => {
        const errors: IDataErrors[] = [];
        responseConfirm?.descripcion?.map(error => errors.push({ error }));
        setDataErrorModal(errors);
    };

    /**
     * Action to get data pila
     * @param {number} index - Number index from current slide
     */
    const handleChangeCard = (index: number): void => {
        setCurrentDate(currentPeriods[index]);
        if (currentDate?.id) {
            dispatch(setCustomLoader(true));
        }
    };

    /**
     * Action to get date by current period
     * @returns {string}
     */
    const handleGetDateModal = (): string => {
        const words = currentDate?.name?.split(' ');
        if (words?.length > 0) {
            const [month, year] = words;
            const newNameMonth = month?.slice(0, 3)?.toUpperCase();
            return `${newNameMonth}-${year}`;
        }
        return '';
    };

    /**
     * Action to redirect to buy new payroll
     */
    const handleRedirectPayrollPlan = (): void => {
        navigate(getRoute(Routes.PAYROLL_PLANS));
    };

    const handleConfirmSocialBenefitsConfiguration = (
        serviceBonus: string,
        severance: string,
        electronic_payroll_vacation_periodicity: string
    ): void => {
        dispatch(
            postSocialBenefitsConfiguration({
                service_bonus: serviceBonus.toLowerCase(),
                severance: severance.toLowerCase(),
                electronic_payroll_vacation_periodicity: electronic_payroll_vacation_periodicity.toLowerCase(),
            })
        );
        setShowSocialBenefitsModal(false);
    };

    return {
        periods: currentPeriods,
        currentDate,
        dataPayroll,
        confirmed,
        transmitConfirmed,
        errorModal,
        errorGeneratePayrollModal,
        dataErrorModal,
        noveltyTransmisionError,
        buyNewPlan,
        showSocialBenefitsModal,
        socialBenefitsConfigurationValues,
        handleGeneratePayroll,
        handleOpenModalError,
        handleOpenModalErrorGenerate,
        handleChangeCard,
        handleGetDateModal,
        handleRedirectPayrollPlan,
        setShowSocialBenefitsModal,
        handleConfirmSocialBenefitsConfiguration,
    };
};
